import moment from 'moment'
import { compact, filter, get, isFinite, join, map } from 'lodash'

import { TabOrderDetailsViewModel } from '../viewmodels/TabOrderViewModel'
import DeviceSubtypes from './deviceSubtypes'
import { centsToDollars } from './formatters'
import { createOrderParams } from './orderUtils'
import { getSubtotalAmountInCents, getTenderInfo } from './orderDisplay'
import { getValidPayments } from '../VNUtil/VNUtils'
import { isSDKCompatible } from '../VNAndroidSDK/Utils'

const TAXRULES_MIN_APK_VERSION = '2.20.0'

//TODO: get this version right when we have a better idea of when the cash rounding feature will be supported on the APK.
const CASH_ROUNDING_MIN_APK_VERSION = '3.2.0'
const CASH_ROUNDING_DISPLAY_NAME = 'Penny Rounding'

/**
 * Supported Print Types for Cloud and USB Printing
 */
export const PRINT_TYPES = {
  CHECK: 'check',
  RECEIPT: 'receipt', // our default for QS
  RECEIPT_AUTH: 'receipt-auth',
  SUITE_AUTH: 'suite-receipt-auth',
  SUITE_ORDER: 'suite-order',
  SUITE_RECEIPT: 'suite-receipt',
  ORDER: 'order', // not available on cloud
  KITCHEN: 'kitchen', // not available on cloud
  REFUND: 'refund',
}

/**
 * Number of printed receipts
 *   - Cloud use the "websocket_print_counts" configuration
 *   - USB requires multiple separate requests
 *
 * @param {string} type
 * @returns {number}
 */
export const getPrintCount = (type, isAutoPrint, print2Copies) => {
  if (isAutoPrint && type === PRINT_TYPES.RECEIPT && print2Copies) {
    return 2
  }

  switch (type) {
    case PRINT_TYPES.SUITE_AUTH:
    case PRINT_TYPES.RECEIPT_AUTH:
      return 2
    default:
      return 1
  }
}

const displayPriceToCents = (displayPrice) => {
  const chars = displayPrice?.split('')
  const onlyNumbers = filter(chars, (char) => isFinite(parseInt(char)))

  return parseInt(join(onlyNumbers, ''))
}

const getDateAndTime = () => {
  const currentTime = moment()
  const date = currentTime.format('YYYY-MM-DD')
  const time = currentTime.format('hh:mm:ss A')

  return { date, time }
}

const makeLineItemsForPrinter = (order) => {
  const { groupedItems } = TabOrderDetailsViewModel(order, null, false)

  return map(groupedItems, (item) => {
    const { name, quantity } = item
    const modifiers = makeModifierItemsForPrinter(item, quantity)
    const priceInCents = displayPriceToCents(item.price) * quantity // item.price includes modifier pricing

    return {
      modifiers,
      name,
      quantity,
      type: item?.type,
      total: priceInCents,
    }
  })
}

const makeModifierItemsForPrinter = (item, quantity) => {
  const modifiers = map(item.modifiers, (modifier) => {
    if (modifier.refund) return null

    const modifierPriceInCents = get(modifier, 'defaultPriceInCents', 0)

    return {
      name: modifier.name,
      total: modifierPriceInCents * quantity,
      quantity: quantity,
    }
  })

  return compact(modifiers)
}

const makeRefundedLineItemsForPrinter = (order) => {
  const { groupedItems } = TabOrderDetailsViewModel(order, null, true)

  return map(groupedItems, (item) => {
    const { name, quantity } = item
    const modifiers = makeRefundedModifierItemsForPrinter(item, quantity)

    const priceInCents = displayPriceToCents(item.price) * quantity
    const discountInCents = displayPriceToCents(item.discountAmountInCents) * quantity

    return {
      modifiers,
      name,
      quantity,
      type: item?.type,
      total: priceInCents - discountInCents,
    }
  })
}

const makeRefundedModifierItemsForPrinter = (item, quantity) => {
  const modifiers = map(item.modifiers, (modifier) => {
    if (!modifier.refund) return null

    const modifierPriceInCents = modifier?.defaultPriceInCents || 0
    const modifierDiscountAmountInCents = displayPriceToCents(modifier?.discountAmountInCents || '0')
    const total = (modifierPriceInCents - modifierDiscountAmountInCents) * quantity

    return {
      name: modifier.name,
      total: total,
      quantity,
    }
  })

  return compact(modifiers)
}

const getUserName = (order) => {
  const userName = createOrderParams(order)?.userName ?? order.userName

  if (typeof userName !== 'string') return ''

  //in case the userName is just "/" which is a value that can be returned by POSLink set it to a blank space so it does not print
  if (userName?.trim() === '/') return ''

  return userName
}

export const DEFAULT_PRINTERS = {
  NO_PRINTER: {
    uuid: 'none',
    name: 'None',
  },
  USB: {
    uuid: 'usb',
    name: 'USB/Device Printer',
  },
}

const formatName = (name) => {
  // cash orders will not have a name associated with them
  if (!name) return ''

  const firstAndLast = name.split(' ')
  if (firstAndLast.length < 2) {
    return firstAndLast[0]
  }
  return `${firstAndLast[0]} ${firstAndLast[1].charAt(0)}.`
}

export const createPrinterData = ({
  deviceSubtype,
  menu,
  order,
  serviceChargeLegal,
  venueName,
  attendantName,
  target = 'USB',
  receiptType,
  useTaxClasses = false,
  androidSDKVersion,
}) => {
  const { date, time } = getDateAndTime()

  const userName = getUserName(order)
  const deviceType = DeviceSubtypes.printerDeviceType(deviceSubtype)

  const lineItems = makeLineItemsForPrinter(order)
  const tenderInfo = getTenderInfo(order)
  const paymentDescription = tenderInfo?.cardIssuer
    ? `${tenderInfo?.cardIssuer} ${tenderInfo?.displayText}`
    : tenderInfo?.displayText

  const payload = {
    venueName,
    date,
    deviceType,
    lineItems,
    menuName: menu.name,
    orderNumber: order.orderNumber,
    receiptType,
    subtotal: getSubtotalAmountInCents(order),
    target,
    time,
    total: order.amountInCents,
    userNotes: order?.userNotes,
    paymentTip: centsToDollars(order.tipAmountInCents ?? 0),
    isMobileOrder: !!order?.isRichCheckout,
    attendantName: formatName(attendantName),
  }

  if (userName) {
    payload.userName = userName
  }

  if (order?.discountAmountInCents !== 0) {
    payload.discount = Math.abs(order.discountAmountInCents)
  }

  if (isSDKCompatible(androidSDKVersion, TAXRULES_MIN_APK_VERSION)) {
    if (useTaxClasses) {
      const taxRulesBreakdown = order?.taxRulesBreakdown || {}
      payload.taxRules = Object.values(taxRulesBreakdown).map((taxRule) => {
        return {
          name: taxRule.taxRuleName,
          total: taxRule.taxRuleAmountInCents,
        }
      })
    } else if (order.taxAmountInCents !== 0) {
      payload.taxRules = [
        {
          name: 'Sales Tax',
          total: order.taxAmountInCents,
        },
      ]
    }
  } else if (order.taxAmountInCents !== 0) {
    payload.taxTotal = order.taxAmountInCents
  }

  if (order.taxExempt && isSDKCompatible(androidSDKVersion, TAXRULES_MIN_APK_VERSION)) {
    payload.taxRules = [
      {
        total: 0,
        rate: null,
        name: 'Tax (Exempt)',
        amount: 'Exempt',
      },
    ]
  }

  if (order?.serviceFee?.total > 0) {
    payload.serviceFees = [order.serviceFee]
    payload.postTotalNote = serviceChargeLegal
  }

  payload.payments = map(getValidPayments(createOrderParams(order)?.payments ?? order?.payments ?? []), (payment) => {
    return {
      total: payment.amountInCents,
      description: payment?.shortDescription ?? paymentDescription,
    }
  })

  if (order.cashRounded) {
    const roundingAmount = order?.cashRoundingInCents || 0

    //For APK versions that support it, we want to show the cash rounding as a line item. For older versions, we will show it as a tax rule with $0 tax to ensure it shows on the receipt without needing to change the layout
    if (isSDKCompatible(androidSDKVersion, CASH_ROUNDING_MIN_APK_VERSION)) {
      const cashRoundingLineItem = {
        modifiers: [],
        name: CASH_ROUNDING_DISPLAY_NAME,
        quantity: 1,
        type: 'CashRoundingLineItem',
        total: roundingAmount,
      }
      payload.lineItems = [...payload.lineItems, cashRoundingLineItem]
    } else {
      payload.taxRules = [
        ...(payload?.taxRules || []),
        {
          total: roundingAmount,
          name: CASH_ROUNDING_DISPLAY_NAME,
        },
      ]
    }
  }

  return payload
}

export const createRefundPrinterData = ({
  deviceSubtype,
  menu,
  order,
  serviceChargeLegal,
  venueName,
  attendantName,
  target = 'USB',
  receiptType,
  useTaxClasses = false,
  androidSDKVersion,
}) => {
  const { date, time } = getDateAndTime()
  const userName = getUserName(order)
  const deviceType = DeviceSubtypes.printerDeviceType(deviceSubtype)

  const lineItems = makeRefundedLineItemsForPrinter(order)

  const refundedCashRoundingInCents = order?.refundedCashRoundingInCents || 0
  const subtotal = displayPriceToCents(order.refundedSubtotal) - refundedCashRoundingInCents
  const total = displayPriceToCents(order.refundedTotal)
  const paymentTip = `$${order.refundedTip}`

  const payload = {
    venueName,
    date,
    deviceType,
    lineItems,
    menuName: menu.name,
    orderNumber: order.orderNumber,
    receiptType,
    subtotal,
    target,
    time,
    total,
    userNotes: order?.userNotes,
    paymentTip,
    isMobileOrder: !!order?.isRichCheckout,
    attendantName: formatName(attendantName),
  }

  if (userName) {
    payload.userName = userName
  }

  if (isSDKCompatible(androidSDKVersion, TAXRULES_MIN_APK_VERSION)) {
    if (useTaxClasses) {
      const refundedTaxRulesBreakdown = order?.refundedTaxRulesBreakdown || {}
      payload.taxRules = Object.values(refundedTaxRulesBreakdown).map((taxRule) => {
        return {
          name: taxRule.taxRuleName,
          total: taxRule.taxRuleAmountInCents,
        }
      })
    } else if (order.refundedTax > 0) {
      payload.taxRules = [
        {
          name: 'Sales Tax',
          total: displayPriceToCents(order.refundedTax),
        },
      ]
    }
  } else if (order.refundedTax > 0) {
    payload.taxTotal = displayPriceToCents(order.refundedTax)
  }

  if (order?.refundedServiceFee?.total > 0) {
    payload.serviceFees = [order.refundedServiceFee]
    payload.postTotalNote = serviceChargeLegal
  }

  payload.payments = map(getValidPayments(order.refundedPayments || []), (refundedPayment) => {
    return {
      total: displayPriceToCents(refundedPayment.total),
      description: refundedPayment.description,
    }
  })

  if (order.refundCashRounded) {
    //For APK versions that support it, we want to show the cash rounding as a line item. For older versions, we will show it as a tax rule with $0 tax to ensure it shows on the receipt without needing to change the layout
    if (isSDKCompatible(androidSDKVersion, CASH_ROUNDING_MIN_APK_VERSION)) {
      const cashRoundingLineItem = {
        modifiers: [],
        name: CASH_ROUNDING_DISPLAY_NAME,
        quantity: 1,
        type: 'CashRoundingLineItem',
        total: refundedCashRoundingInCents,
      }
      payload.lineItems = [...payload.lineItems, cashRoundingLineItem]
    } else {
      payload.taxRules = [
        ...(payload?.taxRules || []),
        {
          total: refundedCashRoundingInCents * -1, //platform sends us the same refunded as in the original receipt, so we need to negate it to show the correct amount on the refund receipt
          name: CASH_ROUNDING_DISPLAY_NAME,
        },
      ]
    }
  }

  return payload
}

export default createPrinterData
